import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { CurrencyModel, FxRate } from '@pinnakl/poems/domain';
import { withDevtools } from '@pinnakl/shared/signal-store';
import { getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { CommonDataApiService } from './common-data-api.service';

interface CommonDataState {
  fxRates: FxRate[] | null;
  fxRatesLoading: boolean;
  currencies: CurrencyModel[];
  currenciesLoading: boolean;
  currenciesLoaded: boolean;
}

const initialState: CommonDataState = {
  fxRates: null,
  fxRatesLoading: false,
  currencies: [],
  currenciesLoading: false,
  currenciesLoaded: false,
};

export const PoemsCommonDataStore = signalStore(
  { providedIn: 'root' },
  withDevtools('poemsCommonData'),
  withState(initialState),
  withProps(store => ({
    fxRates$: toObservable(store.fxRates),
  })),
  withMethods((store, api = inject(CommonDataApiService), toast = inject(PinnaklUIToastMessage)) => ({
    async loadFxRates(forceRefetch = false): Promise<void> {
      if (!forceRefetch && (store.fxRates() !== null || store.fxRatesLoading())) return;
      patchState(store, { fxRatesLoading: true });
      try {
        const fxRates = await api.getFxRates();
        patchState(store, { fxRates, fxRatesLoading: false });
      } catch (error) {
        toast.error(getErrorMessage('Something went wrong during fx rates fetch', error));
        patchState(store, { fxRatesLoading: false });
      }
    },
    setFxRates(fxRates: FxRate[]): void {
      patchState(store, { fxRates });
    },
    async loadCurrencies(forceRefetch = false): Promise<void> {
      if (!forceRefetch && (store.currenciesLoaded() || store.currenciesLoading())) return;
      patchState(store, { currenciesLoading: true });
      try {
        const currencies = await api.getCurrencies();
        patchState(store, { currencies, currenciesLoading: false, currenciesLoaded: true });
      } catch (error) {
        toast.error(getErrorMessage('Something went wrong during currencies fetch', error));
        patchState(store, { currenciesLoading: false });
      }
    },
  })),
);
